import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Users, Target, ArrowRight } from 'lucide-react';

const AboutPage = () => {
  return (
    <div style={{ paddingTop: '80px' }}>
      {/* Hero Section */}
      <section style={{
        background: 'var(--gradient-hero-subtle)',
        padding: '5rem 1.5rem',
        textAlign: 'center'
      }} className="!mx-[25px]">
        <div className="container" style={{ maxWidth: '900px' }}>
          <h1 className="heading-hero" style={{ marginBottom: '1.5rem' }}>
            Who We Are
          </h1>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
            The VEC helps entrepreneurs lead intentionally and grow sustainably
          </p>
        </div>
      </section>

      {/* Core Belief */}
      <section className="section" style={{ background: 'var(--bg-page)' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
            <h2 className="heading-1" style={{ marginBottom: '1.5rem' }}>
              Our Core Belief
            </h2>
            <div style={{
              background: 'var(--accent-pink-200)',
              padding: '2rem',
              borderRadius: '0.75rem',
              marginBottom: '1.5rem',
              border: '2px solid rgba(0, 0, 0, 0.05)'
            }}>
              <p className="heading-2" style={{ fontStyle: 'italic', margin: 0 }}>
                "When you are well, your business becomes well."
              </p>
            </div>
            <p className="body-medium" style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
              We are a wellness-first business strategy company that believes in human-centered entrepreneurship. Your well-being isn't separate from your business success—it's the foundation of it.
            </p>
            <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
              We oppose hustle culture and burnout. Instead, we help you build systems with soul and execute with sacred intention.
            </p>
          </div>
        </div>
      </section>

      {/* Real Partnership */}
      <section className="section !mx-[25px]" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
            <div style={{
              width: '3rem',
              height: '3rem',
              background: 'var(--text-primary)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '1.5rem',
              margin: '0 auto 1.5rem'
            }}>
              <Users size={24} color="white" />
            </div>
            <h2 className="heading-1" style={{ marginBottom: '1.5rem' }}>
              Real Partnership
            </h2>
            <p className="body-large" style={{ marginBottom: '1.5rem', color: 'var(--text-secondary)' }}>
              We don't just give you a strategy document and wish you well. We give you the strategy <strong>AND</strong> implement it with you.
            </p>
            <p className="body-medium" style={{ marginBottom: '1.5rem', color: 'var(--text-secondary)' }}>
              That's the difference. We are in it with you—from vision to execution, from chaos to clarity, from idea to income.
            </p>
            <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
              Our team becomes an extension of yours, working alongside you to build the business you truly want.
            </p>
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="section" style={{ background: 'var(--bg-page)' }}>
        <div className="container !mx-[10px]">
          <div style={{ textAlign: 'center', marginBottom: '3rem', maxWidth: '800px', margin: '0 auto 3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              What Makes Us Different
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              We blend business strategy with wellness practices for sustainable growth
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem' }}>
            <div className="card accent-purple" style={{ padding: '2rem' }}>
              <div style={{
                width: '3rem',
                height: '3rem',
                background: 'white',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '1.5rem'
              }}>
                <Heart size={24} color="var(--accent-purple-400)" />
              </div>
              <h3 className="heading-3" style={{ marginBottom: '1rem' }}>
                Human-Centered Approach
              </h3>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                We see you as a whole person, not just a business owner. Your well-being directly impacts your business success.
              </p>
            </div>

            <div className="card accent-blue" style={{ padding: '2rem' }}>
              <div style={{
                width: '3rem',
                height: '3rem',
                background: 'white',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '1.5rem'
              }}>
                <Target size={24} color="var(--accent-blue-400)" />
              </div>
              <h3 className="heading-3" style={{ marginBottom: '1rem' }}>
                Strategy + Implementation
              </h3>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                We don't stop at planning. We work with you to execute, implement, and bring your vision to life.
              </p>
            </div>

            <div className="card accent-orange" style={{ padding: '2rem' }}>
              <div style={{
                width: '3rem',
                height: '3rem',
                background: 'white',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '1.5rem'
              }}>
                <Users size={24} color="var(--accent-orange-400)" />
              </div>
              <h3 className="heading-3" style={{ marginBottom: '1rem' }}>
                Community & Support
              </h3>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                Through our Entrepreneur Wellness Club, you're never alone. Join a community of like-minded founders.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How We Help */}
      <section className="section !mx-[12px]" style={{ background: 'var(--bg-section)' }}>
        <div className="container !ml-[200px] !mr-[200px] !px-[0px]" style={{ maxWidth: '900px', textAlign: 'center' }}>
          <h2 className="heading-1" style={{ marginBottom: '1.5rem' }}>
            How We Help You Succeed
          </h2>
          <p className="body-large" style={{ marginBottom: '3rem', color: 'var(--text-secondary)' }}>
            Our approach is simple: clarity, structure, and sustainable growth
          </p>

          <div style={{ display: 'grid', gap: '1.5rem', textAlign: 'left' }}>
            {[
            {
              title: '1. We Listen & Understand',
              description: 'Every business is unique. We start by understanding your vision, values, and current challenges.'
            },
            {
              title: '2. We Create Your Blueprint',
              description: 'Custom strategies tailored to your stage, industry, and goals. No cookie-cutter solutions.'
            },
            {
              title: '3. We Implement Together',
              description: 'From processes to tech setup to team building—we work alongside you every step.'
            },
            {
              title: '4. We Support Your Growth',
              description: 'Ongoing support through coaching, workshops, and our wellness-focused membership community.'
            }].
            map((step, index) =>
            <div key={index} className="card" style={{ padding: '2rem', display: 'flex', gap: '1.5rem', alignItems: 'flex-start' }}>
                <div style={{
                minWidth: '3rem',
                height: '3rem',
                background: 'var(--accent-grey-200)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.25rem',
                fontWeight: '600'
              }}>
                  {index + 1}
                </div>
                <div>
                  <h3 className="heading-3" style={{ marginBottom: '0.5rem' }}>
                    {step.title}
                  </h3>
                  <p className="body-medium" style={{ color: 'var(--text-secondary)', margin: 0 }}>
                    {step.description}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section !ml-[11px] !mr-[11px]" style={{ background: 'var(--gradient-hero-subtle)' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 className="heading-1" style={{ marginBottom: '1.5rem' }}>
            Ready to work together?
          </h2>
          <p className="body-large" style={{ marginBottom: '2rem', color: 'var(--text-secondary)', maxWidth: '700px', marginLeft: 'auto', marginRight: 'auto' }}>
            Let's build your business with intention, clarity, and inner peace.
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/products" className="btn-primary" style={{ gap: '0.5rem' }}>
              View Services
              <ArrowRight size={16} />
            </Link>
            <Link to="/contact" className="btn-secondary">
              Book a Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>);

};

export default AboutPage;